import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbsenceModel, AbsenceStatus, BulkUpdateAbsenceRequest } from '@app/reducers/orm/absence/absence.model';
import { AbsenceService } from '@app/reducers/orm/absence/absence.service';
import { AbsenteeOptionModel } from '@app/reducers/orm/absentee-option/absentee-option.model';
import { getAbsenteeOptions } from '@app/reducers/orm/absentee-option/absentee-option.service';
import { ContractModel } from '@app/reducers/orm/contract/contract.model';
import { ContractService } from '@app/reducers/orm/contract/contract.service';
import { DepartmentModel } from '@app/reducers/orm/department/department.model';
import { getActiveDepartments } from '@app/reducers/orm/department/department.service';
import { getActiveEmployees } from '@app/reducers/orm/employee/employee.selectors';
import { ScheduleService } from '@app/reducers/orm/schedule/schedule.service';
import { getActiveShifts, ShiftService } from '@app/reducers/orm/shift/shift.service';
import { getActiveTeams } from '@app/reducers/orm/team/team.service';
import { TimesheetService } from '@app/reducers/orm/timesheet/timesheet.service';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { ToastService } from '@sb/ui/toast/toast.service';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { addDays, eachDayOfInterval, subDays } from 'date-fns';
import { lastValueFrom, Observable } from 'rxjs';

import { generateAbsence, generateAbsenceSlots, generateBulkAbsences } from './dev-tools-absence-generator';
import { DevToolsDataGenerator } from './dev-tools-data-generator';
import { resolveAssignments, resolveTeamShiftOptions } from './dev-tools-hydration.helpers';
import { HydrationConfig, ShiftAssignment, TeamShiftOption } from './dev-tools-hydration.models';
import { getRandomElement } from './dev-tools-mock-data';
import { DevToolsRateLimiter } from './dev-tools-rate-limiter';

/**
 * Generates activity test data (scheduled shifts, timesheets, absences) for the
 * employees currently in the store. Counterpart to DevToolsScaffoldService, which
 * generates the organisational structure these entities hang off of.
 */
@Injectable()
export class DevToolsHydrationService {
  #store = inject(Store<AppState>);
  #scheduleService = inject(ScheduleService);
  #shiftService = inject(ShiftService);
  #timesheetService = inject(TimesheetService);
  #absenceService = inject(AbsenceService);
  #contractService = inject(ContractService);
  #toastService = inject(ToastService);
  #destroyRef = inject(DestroyRef);

  #activeEmployees = this.#store.selectSignal(getActiveEmployees);
  #activeTeams = this.#store.selectSignal(getActiveTeams);
  #activeShifts = this.#store.selectSignal(getActiveShifts);
  #activeDepartments = this.#store.selectSignal<DepartmentModel[]>(getActiveDepartments);
  #absenteeOptions = this.#store.selectSignal<AbsenteeOptionModel[]>(getAbsenteeOptions);

  #shiftTemplatesCreated = 0;
  #scheduledShiftsCreated = 0;
  #timesheetsCreated = 0;
  #absencesCreated = 0;
  #failedRequests = 0;
  #rateLimiter = inject(DevToolsRateLimiter);

  /**
   * Creates the requested amounts of activity data. Best-effort: entity types whose
   * prerequisites are missing are skipped with a toast, and individual failed
   * requests are counted instead of aborting the run.
   */
  async hydrate(config: HydrationConfig, onProgress: (message: string) => void): Promise<void> {
    this.#shiftTemplatesCreated = 0;
    this.#scheduledShiftsCreated = 0;
    this.#timesheetsCreated = 0;
    this.#absencesCreated = 0;
    this.#failedRequests = 0;

    const employees = this.#activeEmployees();

    if (employees.length === 0) {
      this.#toastService.openDanger('No employees available to generate activity data for');
      return;
    }

    const userIds = employees.map((employee) => employee.id).filter((employeeId): employeeId is string => !!employeeId);

    // Templates first — the batch response updates the store, so shifts and
    // timesheets generated in the same run can use the new templates.
    await this.#handleShiftTemplates(config.shiftTemplatesToCreate, onProgress);

    const assignments = resolveAssignments(employees, this.#activeTeams(), this.#activeShifts());
    const teamShiftOptions = resolveTeamShiftOptions(this.#activeTeams(), this.#activeShifts());

    await this.#handleScheduledShifts(config.scheduledShiftsToCreate, userIds, teamShiftOptions, onProgress);
    await this.#handleTimesheets(config.timesheetsToCreate, assignments, onProgress);
    await this.#handleAbsences(config.absencesToCreate, userIds, onProgress);

    this.#showSummary();
  }

  async #handleShiftTemplates(count: number, onProgress: (message: string) => void): Promise<void> {
    if (count === 0) {
      return;
    }

    const departmentIds = this.#activeDepartments()
      .map((department) => department.id)
      .filter((departmentId): departmentId is string => !!departmentId);

    if (departmentIds.length === 0) {
      this.#toastService.openDanger('No departments available — skipped shift templates');
      return;
    }

    const templates = Array.from({ length: count }, () =>
      DevToolsDataGenerator.generateShiftTemplate(getRandomElement(departmentIds)),
    );

    // The batch endpoint creates all templates in a single request
    await this.#runRequests(
      [templates],
      { entityLabel: 'shift templates', apiPath: 'shifts/batch', entityCountOf: (batch) => batch.length },
      onProgress,
      async (batch) => {
        await this.#toPromise(this.#shiftService.batch(batch));
        this.#shiftTemplatesCreated += batch.length;
      },
    );
  }

  async #handleScheduledShifts(
    count: number,
    userIds: string[],
    teamShiftOptions: TeamShiftOption[],
    onProgress: (message: string) => void,
  ): Promise<void> {
    if (count === 0) {
      return;
    }

    if (teamShiftOptions.length === 0) {
      this.#toastService.openDanger('No team with a shift template available — skipped shifts');
      return;
    }

    const payloads = DevToolsDataGenerator.generateScheduledShifts(userIds, teamShiftOptions, count);

    await this.#runRequests(
      payloads,
      {
        entityLabel: 'shifts',
        apiPath: 'rosters/',
        entityCountOf: (payload) => payload.user_id.length,
      },
      onProgress,
      async (payload) => {
        await this.#toPromise(this.#scheduleService.add(payload));
        this.#scheduledShiftsCreated += payload.user_id.length;
      },
    );
  }

  async #handleTimesheets(
    count: number,
    assignments: ShiftAssignment[],
    onProgress: (message: string) => void,
  ): Promise<void> {
    if (count === 0) {
      return;
    }

    if (assignments.length === 0) {
      this.#toastService.openDanger('No employees with a shift template available — skipped timesheets');
      return;
    }

    onProgress('Checking contracts...');

    const contractedDatesByUser = await this.#loadContractedDates();
    const slots = DevToolsDataGenerator.generateTimesheetSlots(assignments, count, contractedDatesByUser);

    if (slots.length === 0) {
      this.#toastService.openDanger('No employees with a contract in the past weeks — skipped timesheets');
      return;
    }

    if (slots.length < count) {
      this.#toastService.openInfo(
        `Only ${slots.length} of ${count} timesheets can be created — max one timesheet per employee per contracted day in the past 4 weeks`,
      );
    }

    await this.#runRequests(
      slots,
      { entityLabel: 'a mix of approved/pending/declined timesheets', apiPath: 'timesheets/' },
      onProgress,
      async (slot) => {
        const payload = DevToolsDataGenerator.generateTimesheet(slot);
        await this.#toPromise(this.#timesheetService.add(payload));
        this.#timesheetsCreated += 1;
      },
    );
  }

  async #handleAbsences(count: number, userIds: string[], onProgress: (message: string) => void): Promise<void> {
    if (count === 0) {
      return;
    }

    const absenteeOptions = this.#absenteeOptions();

    if (absenteeOptions.length === 0) {
      this.#toastService.openDanger('No absence types available — skipped absences');
      return;
    }

    onProgress('Checking existing absences...');

    const occupiedDatesByUser = await this.#loadOccupiedAbsenceDates();
    const slots = generateAbsenceSlots(userIds, count, occupiedDatesByUser);

    if (slots.length < count) {
      this.#toastService.openInfo(
        `Only ${slots.length} of ${count} absences can be created — max one absence per employee per week across ±4 weeks, avoiding existing absences`,
      );
    }

    // Approved absences go through the bulk endpoint (one request per week covering
    // many users — the backend forces them to Approved anyway). Pending/Declined
    // ones need individual creates as the bulk endpoint cannot set a status.
    const approvedSlots = slots.filter((slot) => slot.status === AbsenceStatus.APPROVED);
    const reviewSlots = slots.filter((slot) => slot.status !== AbsenceStatus.APPROVED);

    const bulkPayloads = generateBulkAbsences(approvedSlots, absenteeOptions);
    const bulkUserCount = (payload: BulkUpdateAbsenceRequest) => (payload.UserHours ?? payload.UserDays ?? []).length;

    await this.#runRequests(
      bulkPayloads,
      { entityLabel: 'approved absences', apiPath: 'absentees/bulk', entityCountOf: bulkUserCount },
      onProgress,
      async (payload) => {
        await this.#toPromise(this.#absenceService.bulkUpdateAbsentees(payload));
        this.#absencesCreated += bulkUserCount(payload);
      },
    );

    await this.#runRequests(
      reviewSlots,
      { entityLabel: 'a mix of pending/declined absences', apiPath: 'absentees/' },
      onProgress,
      async (slot) => {
        const payload = generateAbsence(slot, getRandomElement(absenteeOptions));
        await this.#toPromise(this.#absenceService.add(payload));
        this.#absencesCreated += 1;
      },
    );
  }

  /**
   * Loads contracts over the timesheet window (GET requests are not rate limited)
   * and expands them into a per-user set of contracted dates — the backend rejects
   * timesheets on dates without a contract. Returns undefined when contracts could
   * not be loaded, in which case no filtering is applied (best-effort).
   */
  async #loadContractedDates(): Promise<Map<string, Set<string>> | undefined> {
    const minDate = toISODateString(subDays(new Date(), 29));
    const maxDate = toISODateString(new Date());

    try {
      const response = await this.#toPromise<{ entities?: { contracts?: Record<string, ContractModel> } }>(
        this.#contractService.load({ minDate, maxDate }, false),
      );

      const contractedDatesByUser = new Map<string, Set<string>>();

      Object.values(response?.entities?.contracts ?? {}).forEach((contract) => {
        // Clamp to the window — contracts can span years, and open-ended ones have no enddate
        const contractStart = contract.startdate > minDate ? contract.startdate : minDate;
        const contractEndRaw = contract.enddate || maxDate;
        const contractEnd = contractEndRaw < maxDate ? contractEndRaw : maxDate;

        if (contractStart > contractEnd) {
          return;
        }

        const contractedDates = contractedDatesByUser.get(contract.user_id) ?? new Set<string>();

        eachDayOfInterval({ start: new Date(contractStart), end: new Date(contractEnd) }).forEach((contractedDate) =>
          contractedDates.add(toISODateString(contractedDate)),
        );

        contractedDatesByUser.set(contract.user_id, contractedDates);
      });

      return contractedDatesByUser;
    } catch (error) {
      // Best-effort: without contract data, generation continues unfiltered and
      // conflicting requests fail individually (counted, not fatal).
      console.error('Failed to load contracts:', error);
      return undefined;
    }
  }

  /**
   * Loads existing absences around today (GET requests are not rate limited) and
   * expands every non-declined one into a per-user set of occupied dates, so newly
   * generated absences never overlap them.
   */
  async #loadOccupiedAbsenceDates(): Promise<Map<string, Set<string>>> {
    const minDate = toISODateString(subDays(new Date(), 35));
    const maxDate = toISODateString(addDays(new Date(), 35));

    const occupiedDatesByUser = new Map<string, Set<string>>();

    try {
      const response = await this.#toPromise<{ entities?: { absence?: Record<string, AbsenceModel> } }>(
        this.#absenceService.load({ minDate, maxDate }, false),
      );

      Object.values(response?.entities?.absence ?? {})
        .filter((absence) => absence.status !== AbsenceStatus.DECLINED)
        .forEach((absence) => {
          // Open-ended absences have no enddate — they occupy the rest of the window
          const absenceEnd = absence.enddate || maxDate;
          const occupiedDates = occupiedDatesByUser.get(absence.user_id) ?? new Set<string>();

          eachDayOfInterval({ start: new Date(absence.startdate), end: new Date(absenceEnd) }).forEach((occupiedDate) =>
            occupiedDates.add(toISODateString(occupiedDate)),
          );

          occupiedDatesByUser.set(absence.user_id, occupiedDates);
        });
    } catch (error) {
      // Best-effort: without the existing absences, generation continues and
      // conflicting requests fail individually (counted, not fatal).
      console.error('Failed to load existing absences:', error);
    }

    return occupiedDatesByUser;
  }

  /**
   * Runs the worker over the payloads one at a time — parallel requests can
   * deadlock on the backend — while respecting the API rate limit for the
   * given path. Failed payloads are counted, not fatal.
   *
   * Progress counts entities, not requests: one grouped roster payload can
   * create several scheduled shifts, so `entityCountOf` reports how many
   * entities a payload covers (defaults to one per payload).
   */
  async #runRequests<TPayload>(
    payloads: TPayload[],
    requestContext: { entityLabel: string; apiPath: string; entityCountOf?: (payload: TPayload) => number },
    onProgress: (message: string) => void,
    worker: (payload: TPayload) => Promise<void>,
  ): Promise<void> {
    const entityCountOf = requestContext.entityCountOf ?? (() => 1);
    const totalEntities = payloads.reduce((entitySum, payload) => entitySum + entityCountOf(payload), 0);
    let processedEntities = 0;

    for (const payload of payloads) {
      processedEntities += entityCountOf(payload);
      onProgress(`Creating ${requestContext.entityLabel} (${processedEntities}/${totalEntities})...`);

      await this.#rateLimiter.waitForSlot(requestContext.apiPath);

      try {
        await worker(payload);
      } catch (error) {
        console.error('Dev tools hydration request failed:', error);
        this.#failedRequests += 1;
      }
    }
  }

  #toPromise<T>(observable: Observable<T>): Promise<T> {
    return lastValueFrom(observable.pipe(takeUntilDestroyed(this.#destroyRef)));
  }

  #showSummary(): void {
    const summaryParts: string[] = [];

    if (this.#shiftTemplatesCreated > 0) {
      summaryParts.push(`${this.#shiftTemplatesCreated} shift template${this.#shiftTemplatesCreated !== 1 ? 's' : ''}`);
    }
    if (this.#scheduledShiftsCreated > 0) {
      summaryParts.push(`${this.#scheduledShiftsCreated} shift${this.#scheduledShiftsCreated !== 1 ? 's' : ''}`);
    }
    if (this.#timesheetsCreated > 0) {
      summaryParts.push(`${this.#timesheetsCreated} timesheet${this.#timesheetsCreated !== 1 ? 's' : ''}`);
    }
    if (this.#absencesCreated > 0) {
      summaryParts.push(`${this.#absencesCreated} absence${this.#absencesCreated !== 1 ? 's' : ''}`);
    }

    const failureSuffix = this.#failedRequests > 0 ? ` (${this.#failedRequests} request(s) failed)` : '';

    if (summaryParts.length === 0) {
      this.#toastService.openInfo(`No activity data generated${failureSuffix}`);
    } else {
      this.#toastService.openSuccess(`Generated ${summaryParts.join(', ')}${failureSuffix}`);
    }
  }
}
